/**
 * Support/Resistance levels + double-top/bottom patterns — pure, volatility-normalized.
 *
 * Everything here is index-based and side-effect free (callers attach timestamps/symbol
 * downstream). All thresholds are expressed in ATR (or % fallback), never absolute dollars.
 * Reuses: findPivots (./pivots) and the Wilder-ATR algorithm from thoth/thothTrailPrep
 * (re-implemented here parameterized by period — same math as computeAtr14).
 *
 * Pipeline:
 *   bars → findPivots (confirmed only) → cluster by ATR-relative ε (1-D DBSCAN / single-link)
 *        → score (touches · recency · volume) → lifecycle (age-out / break / polarity-flip)
 *   bars → confirmed pivots → double-top/bottom with ATR-relative depth + neckline-break confirm
 */
import type { OHLCVBar } from '@/lib/signals/doubleBottomDetector';
import { findPivots, type Pivot } from '@/lib/signals/pivots';
import { computeVolumeProfile, volumeStrengthAt, nearestHvn, type VolumeProfile } from '@/lib/signals/volumeProfile';

// ── Config ───────────────────────────────────────────────────────────────────
export interface LevelsConfig {
  pivotOrder: number;           // N left/right bars for a pivot (default 3)
  atrPeriod: number;            // ATR period (default 14)
  clusterAtrMult: number;       // DBSCAN ε = clusterAtrMult × ATR (default 0.5)
  fallbackPct: number;          // ε = fallbackPct% of price when ATR is unavailable (default 0.4)
  minTouches: number;           // min confirmed pivots to form a level (default 2)
  breakAtrMult: number;         // decisive break = close beyond by breakAtrMult × ATR (default 0.75)
  ageBars: number;              // drop a level this many bars after its last touch (default 300)
  recencyHalfLifeBars: number;  // recency weight half-life, in bars (default 120)
  dtTolAtrMult: number;         // two pivots "equal" within dtTolAtrMult × ATR (default 0.6)
  dtTolFloorPct: number;        // FLOOR under the "equal" tolerance, % of price (default 0.5) — on
                                // calm minute charts ATR collapses to pennies, so an obvious $2.19/
                                // $2.20 double reads as two levels; this keeps it human-sensible.
  dtMinDepthAtrMult: number;    // intervening trough/peak depth ≥ this × ATR (default 1.0)
  dtMinSep: number;             // min bars between the two pivots (default 5)
  dtMaxSep: number;             // max bars between the two pivots (default 60; TF-scaled by levelsService)
  // ── Volume profile / confluence (Goal 1) ──
  vpBinAtrMult: number;         // VP bin width = vpBinAtrMult × ATR (default 0.25)
  vpLookbackBars: number;       // VP trailing window; 0 = all (SHORT for intraday — set per TF)
  hvnPercentile: number;        // high-volume-node threshold percentile (default 70)
  lvnPercentile: number;        // low-volume-node threshold percentile (default 20)
  vpSpikeWinsorPct: number;     // cap each bar's volume at this percentile (spike robustness, default 95)
  volConfluenceWeight: number;  // bounded boost: strength × (1 + w·volume_strength) (default 0.5)
}

export const DEFAULT_LEVELS_CONFIG: LevelsConfig = {
  pivotOrder: 3,
  atrPeriod: 14,
  clusterAtrMult: 0.5,
  fallbackPct: 0.4,
  minTouches: 2,
  breakAtrMult: 0.75,
  ageBars: 300,
  recencyHalfLifeBars: 120,
  dtTolAtrMult: 0.6,
  dtTolFloorPct: 0.5,
  dtMinDepthAtrMult: 1.0,
  dtMinSep: 5,
  dtMaxSep: 60,
  vpBinAtrMult: 0.25,
  vpLookbackBars: 0,
  hvnPercentile: 70,
  lvnPercentile: 20,
  vpSpikeWinsorPct: 95,
  volConfluenceWeight: 0.5,
};

// ── Output types (the contract entries/exits consume) ─────────────────────────
export interface Level {
  price: number;                       // cluster center (touch-weighted)
  kind: 'support' | 'resistance';      // role relative to the latest close (reflects polarity flips)
  strength: number;                    // touches · recency · volume, ≥ 0
  touches: number;                     // confirmed pivots in the cluster
  firstTouchIdx: number;
  lastTouchIdx: number;
  brokenAtIdx: number | null;          // first decisive break after formation (null = intact)
  flipped: boolean;                    // broke then price is now on the other side (resistance↔support)
  pivotIdxs: number[];
  volume_strength: number;             // [0,1] transacted volume at this price (vs profile POC)
  at_hvn: boolean;                     // sits within tolerance of a high-volume node
}

export interface Pattern {
  kind: 'double_top' | 'double_bottom';
  confirmed: boolean;                  // true ONLY after the neckline break (non-repainting trigger)
  neckline: number;                    // the intervening extreme (trough for tops, peak for bottoms)
  pivots: Pivot[];                     // [p1, intervening, p2]
  breakIdx: number | null;             // bar index where the neckline broke (null = provisional)
  level: number;                       // the shared pivot price (the "double" level)
}

export interface LevelProximity {
  level: Level;
  distanceAtr: number;                 // |price − level| / ATR
  distancePct: number;
  side: 'above' | 'below';             // is the level above or below the queried price
}

// ── ATR (Wilder), period-parameterized (same algorithm as computeAtr14) ───────
export function computeAtr(bars: OHLCVBar[], period = 14): number | null {
  if (bars.length < period + 1) return null;
  const src = bars.length > period * 8 ? bars.slice(-period * 8) : bars;
  const trs: number[] = [];
  for (let i = 1; i < src.length; i++) {
    const { high, low } = src[i]!;
    const prevClose = src[i - 1]!.close;
    trs.push(Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose)));
  }
  if (trs.length < period) return null;
  let atr = 0;
  for (let i = 0; i < period; i++) atr += trs[i]!;
  atr /= period;
  for (let i = period; i < trs.length; i++) atr = (atr * (period - 1) + trs[i]!) / period;
  return atr > 0 ? atr : null;
}

/** ε used for clustering / tolerance, in price units (ATR-relative, % fallback). */
export function atrTolerance(bars: OHLCVBar[], cfg: LevelsConfig = DEFAULT_LEVELS_CONFIG, atr?: number | null): number {
  const a = atr ?? computeAtr(bars, cfg.atrPeriod);
  const lastClose = bars.length ? bars[bars.length - 1]!.close : 0;
  if (a && a > 0) return cfg.clusterAtrMult * a;
  return (cfg.fallbackPct / 100) * lastClose;
}

// ── Support / Resistance ──────────────────────────────────────────────────────
export function computeLevels(
  bars: OHLCVBar[],
  cfg: LevelsConfig = DEFAULT_LEVELS_CONFIG,
  atrIn?: number | null,
  profileIn?: VolumeProfile | null,
): Level[] {
  const n = bars.length;
  if (n < cfg.pivotOrder * 2 + 2) return [];
  const atr = atrIn ?? computeAtr(bars, cfg.atrPeriod);
  const eps = atrTolerance(bars, cfg, atr);
  if (!(eps > 0)) return [];
  const lastIdx = n - 1;
  const lastClose = bars[lastIdx]!.close;
  const breakDist = (atr && atr > 0 ? cfg.breakAtrMult * atr : (cfg.fallbackPct / 100) * lastClose * 2);

  // Volume profile (reused if the caller already built it for the vap summary).
  const profile = profileIn ?? buildProfile(bars, cfg, atr);

  const pivots = findPivots(bars, cfg.pivotOrder).confirmed;
  if (pivots.length < cfg.minTouches) return [];

  // 1-D single-linkage clustering (DBSCAN with minPts=minTouches, ε=eps on sorted prices).
  const sorted = [...pivots].sort((a, b) => a.price - b.price);
  const clusters: Pivot[][] = [];
  let cur: Pivot[] = [];
  for (const p of sorted) {
    if (cur.length === 0 || p.price - cur[cur.length - 1]!.price <= eps) cur.push(p);
    else { clusters.push(cur); cur = [p]; }
  }
  if (cur.length) clusters.push(cur);

  const medVol = medianVolume(bars);
  const levels: Level[] = [];
  for (const cl of clusters) {
    if (cl.length < cfg.minTouches) continue;
    const price = mean(cl.map(p => p.price));
    const idxs = cl.map(p => p.idx).sort((a, b) => a - b);
    const firstTouchIdx = idxs[0]!;
    const lastTouchIdx = idxs[idxs.length - 1]!;

    // Lifecycle: age-out.
    if (lastIdx - lastTouchIdx > cfg.ageBars) continue;

    // Decisive break after the last touch (close beyond by breakDist).
    let brokenAtIdx: number | null = null;
    for (let i = lastTouchIdx + 1; i <= lastIdx; i++) {
      const c = bars[i]!.close;
      if (c > price + breakDist || c < price - breakDist) { brokenAtIdx = i; break; }
    }

    // Role is relative to the latest close — this naturally reflects polarity flips.
    const kind: Level['kind'] = lastClose >= price ? 'support' : 'resistance';

    // GENUINE polarity flip only: the level was broken, price then RETESTED it from the other
    // side, and the current role is the opposite of the pre-break role. (Previously this was
    // just `brokenAtIdx != null` — i.e. "ever broken" — which over a long daily window is
    // nearly every level, so everything rendered as "flipped". A break alone is not a flip.)
    let flipped = false;
    if (brokenAtIdx != null) {
      const brokeUp = bars[brokenAtIdx]!.close > price;             // broke above → was resistance
      const preRole: Level['kind'] = brokeUp ? 'resistance' : 'support';
      let retest = false;
      for (let i = brokenAtIdx + 1; i <= lastIdx; i++) {
        const b = bars[i]!;
        if (b.low <= price + eps && b.high >= price - eps) { retest = true; break; }  // price revisited the level
      }
      flipped = retest && kind !== preRole;
    }

    // Strength = touches · recency · volFactor (existing), then a BOUNDED volume-at-price
    // confluence boost — a level that sits on real transacted size outranks an equal-touch
    // level that doesn't, without overwriting the existing terms.
    const ageSinceTouch = lastIdx - lastTouchIdx;
    const recency = Math.pow(0.5, ageSinceTouch / Math.max(1, cfg.recencyHalfLifeBars));
    const avgTouchVol = mean(cl.map(p => bars[p.idx]?.volume ?? 0));
    const volFactor = medVol > 0 ? 1 + Math.max(-0.4, Math.min(0.6, avgTouchVol / medVol - 1)) : 1;
    const baseStrength = cl.length * recency * volFactor;

    const volume_strength = profile ? volumeStrengthAt(profile, price) : 0;
    const at_hvn = profile ? nearestHvn(profile, price, eps) != null : false;
    const strength = baseStrength * (1 + cfg.volConfluenceWeight * volume_strength);

    levels.push({ price, kind, strength, touches: cl.length, firstTouchIdx, lastTouchIdx, brokenAtIdx, flipped, pivotIdxs: idxs, volume_strength, at_hvn });
  }
  return levels.sort((a, b) => b.strength - a.strength);
}

/** Build the volume profile for a level computation from the LevelsConfig knobs. */
export function buildProfile(bars: OHLCVBar[], cfg: LevelsConfig = DEFAULT_LEVELS_CONFIG, atr?: number | null): VolumeProfile {
  return computeVolumeProfile(bars, {
    binAtrMult: cfg.vpBinAtrMult,
    fallbackPct: cfg.fallbackPct,
    lookbackBars: cfg.vpLookbackBars,
    hvnPercentile: cfg.hvnPercentile,
    lvnPercentile: cfg.lvnPercentile,
    spikeWinsorPct: cfg.vpSpikeWinsorPct,
  }, atr ?? computeAtr(bars, cfg.atrPeriod));
}

// ── Double top / double bottom ────────────────────────────────────────────────
export function detectPatterns(
  bars: OHLCVBar[],
  cfg: LevelsConfig = DEFAULT_LEVELS_CONFIG,
  atrIn?: number | null,
): Pattern[] {
  const n = bars.length;
  if (n < cfg.pivotOrder * 2 + cfg.dtMinSep + 2) return [];
  const atr = atrIn ?? computeAtr(bars, cfg.atrPeriod);
  const lastClose = bars[n - 1]!.close;
  // "Equal-pivot" tolerance: ATR-relative, but never tighter than dtTolFloorPct% of price so a
  // low-ATR minute chart still treats a $2.19/$2.20 pair as ONE shelf (not two levels).
  const tol   = Math.max(
    atr && atr > 0 ? cfg.dtTolAtrMult * atr : (cfg.fallbackPct / 100) * lastClose,
    (cfg.dtTolFloorPct / 100) * lastClose,
  );
  const depthMin = atr && atr > 0 ? cfg.dtMinDepthAtrMult * atr : (cfg.fallbackPct / 100) * lastClose * 2.5;

  const piv = findPivots(bars, cfg.pivotOrder).confirmed;
  const highs = piv.filter(p => p.kind === 'high').sort((a, b) => a.idx - b.idx);
  const lows  = piv.filter(p => p.kind === 'low').sort((a, b) => a.idx - b.idx);

  const out: Pattern[] = [];
  // Double TOP: two highs within tol, an intervening trough ≥ depthMin below, neckline = trough.
  scanPairs(highs, lows, 'high', tol, depthMin, cfg, bars, lastClose, out);
  // Double BOTTOM: two lows within tol, an intervening peak ≥ depthMin above, neckline = peak.
  scanPairs(lows, highs, 'low', tol, depthMin, cfg, bars, lastClose, out);
  return dedupePatterns(out, tol);
}

/**
 * Collapse the C(n,2) explosion: within one consolidation many high-pairs share the same
 * neckline and overlapping windows. Keep one representative per (kind, neckline±tol,
 * overlapping window) — the most RECENT (most actionable), preferring confirmed.
 */
function dedupePatterns(pats: Pattern[], tol: number): Pattern[] {
  const out: Pattern[] = [];
  for (const kind of ['double_top', 'double_bottom'] as const) {
    const ps = pats.filter(p => p.kind === kind)
      .sort((a, b) => (b.breakIdx ?? b.pivots[2]!.idx) - (a.breakIdx ?? a.pivots[2]!.idx)
        || Math.abs(a.pivots[0]!.price - a.pivots[2]!.price) - Math.abs(b.pivots[0]!.price - b.pivots[2]!.price));
    const kept: Pattern[] = [];
    for (const p of ps) {
      const a0 = p.pivots[0]!.idx, a2 = p.pivots[2]!.idx;
      const dup = kept.some(k => Math.abs(k.neckline - p.neckline) <= tol
        && k.pivots[0]!.idx <= a2 && a0 <= k.pivots[2]!.idx);   // overlapping windows
      if (!dup) kept.push(p);
    }
    out.push(...kept);
  }
  return out;
}

function scanPairs(
  same: Pivot[], opp: Pivot[], kind: 'high' | 'low',
  tol: number, depthMin: number, cfg: LevelsConfig,
  bars: OHLCVBar[], lastClose: number, out: Pattern[],
): void {
  const lastIdx = bars.length - 1;
  const seen = new Set<string>();
  for (let a = 0; a < same.length; a++) {
    for (let b = a + 1; b < same.length; b++) {
      const p1 = same[a]!, p2 = same[b]!;
      const sep = p2.idx - p1.idx;
      if (sep < cfg.dtMinSep || sep > cfg.dtMaxSep) continue;
      if (Math.abs(p1.price - p2.price) > tol) continue;

      // Intervening opposite extreme strictly between the two pivots.
      const between = opp.filter(o => o.idx > p1.idx && o.idx < p2.idx);
      if (!between.length) continue;
      const intervening = kind === 'high'
        ? between.reduce((m, o) => (o.price < m.price ? o : m))   // deepest trough
        : between.reduce((m, o) => (o.price > m.price ? o : m));  // highest peak
      const neckline = intervening.price;
      const depth = kind === 'high'
        ? Math.min(p1.price, p2.price) - neckline
        : neckline - Math.max(p1.price, p2.price);
      if (depth < depthMin) continue;   // flat range / shallow dip → NOT a double pattern

      const key = `${p1.idx}-${p2.idx}`;
      if (seen.has(key)) continue;
      seen.add(key);

      // Confirmed ONLY when a later bar CLOSES beyond the neckline (non-repainting break).
      let breakIdx: number | null = null;
      for (let i = p2.idx + 1; i <= lastIdx; i++) {
        const c = bars[i]!.close;
        if (kind === 'high' ? c < neckline : c > neckline) { breakIdx = i; break; }
      }
      out.push({
        kind: kind === 'high' ? 'double_top' : 'double_bottom',
        confirmed: breakIdx != null,
        neckline,
        pivots: [p1, intervening, p2],
        breakIdx,
        level: mean([p1.price, p2.price]),
      });
    }
  }
}

// ── Pattern rendering + cross-TF neckline merge (Goal 2) ──────────────────────
export interface PatternStyle {
  color: string;     // teal = bullish double-bottom, red = bearish double-top, gold-gray = contested
  label: string;     // "DB ↑" / "DT ↓" / "contested"
  dashed: boolean;   // necklines are always dashed (distinct from dotted S/R lines)
  dim: boolean;      // forming (unconfirmed) patterns render dimmed
  side: 'bullish' | 'bearish' | 'neutral';
}

/** Pure pattern→style map so the side/label/color is unambiguous (and unit-testable). */
export function patternStyle(p: { kind: 'double_top' | 'double_bottom' | 'contested'; confirmed: boolean }): PatternStyle {
  if (p.kind === 'contested') {
    // A DT trigger AND a DB support coincide at one price — both sides active, no clean
    // direction. Neutral gold-gray, NO arrow, so we never stamp a false bias on a two-sided price.
    return { color: '#b8a888', label: 'contested', dashed: true, dim: !p.confirmed, side: 'neutral' };
  }
  const isBottom = p.kind === 'double_bottom';
  return {
    color: isBottom ? '#2dd4bf' : '#f87171',   // teal (BUY, breaks up) vs red (SELL, breaks down)
    label: isBottom ? 'DB ↑' : 'DT ↓',
    dashed: true,
    dim: !p.confirmed,
    side: isBottom ? 'bullish' : 'bearish',
  };
}

export type ResolvedPattern = { kind: 'double_top' | 'double_bottom' | 'contested'; neckline: number; level: number; confirmed: boolean };

/**
 * Resolve cross-kind neckline collisions. When a double-top neckline (a swing low / break-
 * down trigger) and a double-bottom neckline (a swing high / break-up trigger) fall within
 * `tol` of one price, that price is CONTESTED — collapse the pair into ONE neutral marker
 * instead of stamping two opposing arrows on the same line. Non-colliding patterns pass through.
 */
export function resolveContested<T extends { kind: string; neckline: number; confirmed: boolean }>(patterns: T[], tol: number): ResolvedPattern[] {
  const tops = patterns.filter(p => p.kind === 'double_top');
  const bottoms = patterns.filter(p => p.kind === 'double_bottom');
  const usedT = new Set<number>(), usedB = new Set<number>();
  const out: ResolvedPattern[] = [];
  for (let i = 0; i < tops.length; i++) {
    for (let j = 0; j < bottoms.length; j++) {
      if (usedB.has(j)) continue;
      if (Math.abs(tops[i]!.neckline - bottoms[j]!.neckline) <= tol) {
        out.push({ kind: 'contested', neckline: (tops[i]!.neckline + bottoms[j]!.neckline) / 2, level: (tops[i]!.neckline + bottoms[j]!.neckline) / 2, confirmed: tops[i]!.confirmed || bottoms[j]!.confirmed });
        usedT.add(i); usedB.add(j);
        break;
      }
    }
  }
  tops.forEach((t, i) => { if (!usedT.has(i)) out.push({ kind: 'double_top', neckline: t.neckline, level: (t as { level?: number }).level ?? t.neckline, confirmed: t.confirmed }); });
  bottoms.forEach((b, j) => { if (!usedB.has(j)) out.push({ kind: 'double_bottom', neckline: b.neckline, level: (b as { level?: number }).level ?? b.neckline, confirmed: b.confirmed }); });
  return out;
}

/**
 * Pick the actionable patterns: gate out STALE ones by recency (break/formation `ts` older
 * than `nowTs - recentMs`), then rank the survivors by PROXIMITY to the current price (a
 * neckline you're about to test matters more than a far one), tie-broken by recency, and keep
 * the top `perSide` of each side. `recentMs <= 0` disables the recency gate.
 */
export function selectActivePatterns<T extends { kind: string; neckline: number; ts?: number }>(
  patterns: T[], opts: { nowTs: number; recentMs: number; currentPrice: number; perSide: number },
): T[] {
  const { nowTs, recentMs, currentPrice, perSide } = opts;
  const fresh = patterns.filter(p => recentMs <= 0 || (p.ts ?? 0) >= nowTs - recentMs);
  const out: T[] = [];
  for (const kind of ['double_top', 'double_bottom'] as const) {
    const side = fresh.filter(p => p.kind === kind)
      .sort((a, b) => Math.abs(a.neckline - currentPrice) - Math.abs(b.neckline - currentPrice) || (b.ts ?? 0) - (a.ts ?? 0));
    out.push(...side.slice(0, perSide));
  }
  return out;
}

/**
 * Merge patterns whose necklines coincide (same kind, within `tol` price) — collapses the
 * duplicate "DT neckline" labels at the identical price, including across timeframes.
 * Prefers confirmed over forming, keeping one representative per coincident neckline.
 */
export function mergeCoincidentNecklines<T extends { kind: string; neckline: number; confirmed: boolean }>(patterns: T[], tol: number): T[] {
  const sorted = [...patterns].sort((a, b) => Number(b.confirmed) - Number(a.confirmed));
  const kept: T[] = [];
  for (const p of sorted) {
    if (kept.some(k => k.kind === p.kind && Math.abs(k.neckline - p.neckline) <= tol)) continue;
    kept.push(p);
  }
  return kept;
}

// ── Proximity ──────────────────────────────────────────────────────────────────
export function distanceToNearestLevel(
  price: number,
  levels: Level[],
  atr: number | null,
): LevelProximity | null {
  if (!levels.length || !(price > 0)) return null;
  let best: Level | null = null;
  let bestDist = Infinity;
  for (const lv of levels) {
    const d = Math.abs(price - lv.price);
    if (d < bestDist) { bestDist = d; best = lv; }
  }
  if (!best) return null;
  return {
    level: best,
    distanceAtr: atr && atr > 0 ? bestDist / atr : Infinity,
    distancePct: (bestDist / price) * 100,
    side: best.price >= price ? 'above' : 'below',
  };
}

// ── small helpers ───────────────────────────────────────────────────────────────
function mean(xs: number[]): number { return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0; }
function medianVolume(bars: OHLCVBar[]): number {
  const vs = bars.map(b => b.volume ?? 0).filter(v => v > 0).sort((a, b) => a - b);
  if (!vs.length) return 0;
  const mid = Math.floor(vs.length / 2);
  return vs.length % 2 ? vs[mid]! : (vs[mid - 1]! + vs[mid]!) / 2;
}
